import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, Send, BookOpen, Loader2, Sparkles, User,
  Mic, MicOff, Volume2, VolumeX, Trash2, Mail, X, Check, Search, Plus,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { AuthContext } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROL_CHIP = {
  profesor:   'bg-blue-100 text-blue-700 border-blue-300',
  estudiante: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  admin:      'bg-purple-100 text-purple-700 border-purple-300',
  pastor:     'bg-amber-100 text-amber-700 border-amber-300',
  graduado:   'bg-slate-100 text-slate-700 border-slate-300',
};
const rolChip = (rol) => ROL_CHIP[(rol || '').toLowerCase()] || 'bg-gray-100 text-gray-600 border-gray-300';

// Web Speech API: detección de soporte
const SpeechRecognition =
  typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const speechSupported = !!SpeechRecognition;
const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

/**
 * Genesis AI — Asistente pedagógico dentro de Sala Docente.
 */
export default function GenesisAITab() {
  const { user, getImageUrl } = useContext(AuthContext);
  const [clases, setClases] = useState([]);
  const [selectedClaseId, setSelectedClaseId] = useState('');
  const [messages, setMessages] = useState([]); // [{role, content, ts, email_draft?}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [listening, setListening] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null); // índice del mensaje siendo leído
  const [emailDraft, setEmailDraft] = useState(null); // {to, cc, subject, body}
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // CC picker
  const [activeUsers, setActiveUsers] = useState([]);
  const [ccPickerOpen, setCcPickerOpen] = useState(false);
  const [ccSearch, setCcSearch] = useState('');
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Cargar clases
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/sala-docente/genesis-ai/clase-libros`, { headers });
        const all = res.data?.clases || [];
        const withBooks = all.filter((c) => (c.libros_count || 0) > 0);
        setClases(withBooks);
        if (withBooks.length > 0 && !selectedClaseId) {
          setSelectedClaseId(withBooks[0].clase_id);
        }
      } catch (_e) {
        try {
          const res2 = await axios.get(`${API}/clases`, { headers });
          const cl = (res2.data || []).map((c) => ({
            clase_id: c.id, clase_titulo: c.titulo || c.nombre, libros_count: 0,
          }));
          setClases(cl);
        } catch (_e2) { /* silent */ }
      }
    })();
  }, []);

  // Cargar historial
  useEffect(() => {
    if (!selectedClaseId) { setMessages([]); return; }
    (async () => {
      setLoadingHistory(true);
      try {
        const res = await axios.get(
          `${API}/sala-docente/genesis-ai/conversaciones?clase_id=${selectedClaseId}`,
          { headers }
        );
        const flat = [];
        for (const m of res.data?.messages || []) {
          if (m.user_message) flat.push({ role: 'user', content: m.user_message, ts: m.timestamp });
          if (m.ai_response) flat.push({ role: 'assistant', content: m.ai_response, ts: m.timestamp });
        }
        setMessages(flat);
      } catch (_e) {
        setMessages([]);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [selectedClaseId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Limpiar TTS al desmontar
  useEffect(() => {
    return () => {
      if (ttsSupported) window.speechSynthesis.cancel();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_e) { /* noop */ }
      }
    };
  }, []);

  const enviar = useCallback(async (textOverride) => {
    const text = (textOverride !== undefined ? textOverride : input).trim();
    if (!text || !selectedClaseId || sending) return;
    const userMsg = { role: 'user', content: text, ts: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/sala-docente/genesis-ai/chat`,
        { clase_id: selectedClaseId, message: text },
        { headers, timeout: 180000 }
      );
      const ai = res.data?.response || 'Sin respuesta.';
      const draft = res.data?.email_draft;
      setMessages((m) => [...m, {
        role: 'assistant',
        content: ai,
        ts: new Date().toISOString(),
        email_draft: draft || null,
      }]);
      // Si vino email_draft, abrir modal
      if (draft) {
        setEmailDraft(draft);
        // Pre-llenar "Para": si el AI lo detectó, usar ese; si no, usar el email del profesor que pide
        const fallbackTo = user?.email || '';
        setEmailForm({
          to: draft.to_missing ? fallbackTo : (draft.to || fallbackTo),
          cc: (draft.cc || []).join(', '),
          subject: draft.subject || '',
          body: draft.body || '',
        });
        if (draft.to_missing) {
          toast.info(fallbackTo
            ? `📧 Genesis preparó un email — usamos tu email (${fallbackTo}) como destinatario por defecto, editá si querés.`
            : '📧 Genesis preparó un email — necesita que indiques el destinatario');
        } else {
          toast.success('📧 Genesis preparó un email — revisalo antes de enviar');
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Error consultando a Genesis AI';
      toast.error(`❌ ${detail}`);
      setMessages((m) => [...m, { role: 'assistant', content: `⚠️ ${detail}`, ts: new Date().toISOString(), error: true }]);
    } finally {
      setSending(false);
    }
  }, [input, selectedClaseId, sending]);

  // ─── Voice input (Web Speech API) ───
  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Tu navegador no soporta dictado por voz. Probá con Chrome, Edge o Safari.');
      return;
    }
    if (listening) {
      try { recognitionRef.current?.stop(); } catch (_e) { /* noop */ }
      setListening(false);
      return;
    }
    try {
      const rec = new SpeechRecognition();
      rec.lang = 'es-UY';
      rec.continuous = false;
      rec.interimResults = true;
      rec.maxAlternatives = 1;
      let finalText = '';
      rec.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalText += transcript + ' ';
          else interim += transcript;
        }
        setInput((finalText + interim).trim());
      };
      rec.onerror = (e) => {
        if (e.error !== 'no-speech' && e.error !== 'aborted') {
          toast.error('Error con el micrófono: ' + e.error);
        }
        setListening(false);
      };
      rec.onend = () => {
        setListening(false);
        // Si quedó texto final, NO auto-enviamos — el profesor confirma con el botón.
      };
      recognitionRef.current = rec;
      rec.start();
      setListening(true);
      toast.info('🎤 Escuchando... (hablá ahora)', { duration: 1500 });
    } catch (e) {
      toast.error('No se pudo iniciar el micrófono: ' + (e.message || e));
      setListening(false);
    }
  };

  // ─── TTS (lectura de respuestas) ───
  const toggleSpeak = (idx, text) => {
    if (!ttsSupported) {
      toast.error('Tu navegador no soporta lectura por voz.');
      return;
    }
    // Si ya está leyendo este o cualquier otro, cancelar
    if (speakingIdx !== null) {
      window.speechSynthesis.cancel();
      if (speakingIdx === idx) {
        setSpeakingIdx(null);
        return;
      }
    }
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'es-UY';
    utter.rate = 1.0;
    utter.pitch = 1.0;
    // Buscar voz en español si está disponible
    const voices = window.speechSynthesis.getVoices();
    const esVoice = voices.find((v) => v.lang?.toLowerCase().startsWith('es'));
    if (esVoice) utter.voice = esVoice;
    utter.onend = () => setSpeakingIdx(null);
    utter.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(utter);
    setSpeakingIdx(idx);
  };

  // ─── Limpiar conversación ───
  const clearConversation = async () => {
    setShowClearConfirm(false);
    try {
      await axios.delete(
        `${API}/sala-docente/genesis-ai/conversaciones?clase_id=${selectedClaseId}`,
        { headers }
      );
      setMessages([]);
      toast.success('🗑️ Conversación borrada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error borrando conversación');
    }
  };

  // ─── Envío de email confirmado ───
  const enviarEmail = async () => {
    const to = (emailForm.to || '').trim();
    if (!to || !to.includes('@')) { toast.error('Email destinatario inválido'); return; }
    if (!emailForm.body.trim()) { toast.error('El cuerpo del email no puede estar vacío'); return; }
    const cc_emails = (emailForm.cc || '')
      .split(',').map((e) => e.trim()).filter((e) => e && e.includes('@'));
    setSendingEmail(true);
    try {
      const res = await axios.post(
        `${API}/sala-docente/genesis-ai/enviar-email`,
        {
          clase_id: selectedClaseId,
          destinatario_email: to,
          cc_emails,
          subject: emailForm.subject,
          body: emailForm.body,
        },
        { headers, timeout: 60000 }
      );
      if (res.data?.preview_mode) {
        toast.info('Modo preview: email NO enviado realmente.');
      } else {
        toast.success(res.data?.message || `Email enviado a ${to}`);
      }
      setEmailDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando email');
    } finally {
      setSendingEmail(false);
    }
  };

  // ─── CC: cargar usuarios activos al abrir el modal ───
  useEffect(() => {
    if (!emailDraft || activeUsers.length > 0) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/users`, { headers });
        const list = (res.data || [])
          .filter((u) => u.activo !== false && u.email && u.email !== user?.email)
          .map((u) => ({
            id: u.id,
            email: u.email,
            rol: u.rol,
            foto_perfil: u.foto_perfil,
            nombre_completo: `${u.nombre || ''} ${u.apellido || ''}`.trim() || u.email,
          }))
          .sort((a, b) => a.nombre_completo.localeCompare(b.nombre_completo, 'es'));
        setActiveUsers(list);
      } catch (_e) { /* silent */ }
    })();
    // eslint-disable-next-line
  }, [emailDraft]);

  // Reset picker state al cerrar modal
  useEffect(() => {
    if (!emailDraft) {
      setCcPickerOpen(false);
      setCcSearch('');
    }
  }, [emailDraft]);

  // Parse CC string → array de emails
  const ccEmails = (emailForm.cc || '')
    .split(',').map((e) => e.trim()).filter(Boolean);
  const ccEmailsSet = new Set(ccEmails.map((e) => e.toLowerCase()));

  const addCcEmail = (email) => {
    if (!email || ccEmailsSet.has(email.toLowerCase())) return;
    const next = [...ccEmails, email].join(', ');
    setEmailForm((f) => ({ ...f, cc: next }));
  };

  const removeCcEmail = (email) => {
    const next = ccEmails.filter((e) => e.toLowerCase() !== email.toLowerCase()).join(', ');
    setEmailForm((f) => ({ ...f, cc: next }));
  };

  const ccUsersFiltrados = activeUsers.filter((u) => {
    const q = ccSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.nombre_completo || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.rol || '').toLowerCase().includes(q);
  });

  const selectedClase = clases.find((c) => c.clase_id === selectedClaseId);
  const hasNoBooks = selectedClase && (selectedClase.libros_count || 0) === 0;

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/30" data-testid="genesis-ai-tab">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white/80 backdrop-blur-sm flex items-center gap-3 shrink-0">
        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-gray-900">Genesis AI</h2>
            <Sparkles className="w-4 h-4 text-amber-500" />
          </div>
          <p className="text-xs text-gray-500 truncate">Asistente pedagógico — uso exclusivo profesores</p>
        </div>
        {messages.length > 0 && (
          <button
            onClick={() => setShowClearConfirm(true)}
            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Borrar conversación"
            data-testid="genesis-clear-btn"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Selector de clase */}
      <div className="px-4 py-3 border-b border-gray-100 bg-white shrink-0">
        <label className="block text-xs font-medium text-gray-500 mb-1">Consultar libros de la clase:</label>
        <select
          value={selectedClaseId}
          onChange={(e) => setSelectedClaseId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          data-testid="genesis-clase-selector"
        >
          {clases.length === 0 && <option value="">— No hay clases con libros cargados —</option>}
          {clases.map((c) => (
            <option key={c.clase_id} value={c.clase_id}>
              {c.clase_titulo} {c.libros_count > 0 ? `(${c.libros_count} libro${c.libros_count > 1 ? 's' : ''})` : '(sin libros)'}
            </option>
          ))}
        </select>
        {hasNoBooks && (
          <p className="text-xs text-amber-600 mt-2">
            ⚠️ Esta clase aún no tiene libros vinculados. Pedile al admin que cargue el material.
          </p>
        )}
      </div>

      {/* Mensajes */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="genesis-messages">
        {loadingHistory && (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
        )}
        {!loadingHistory && messages.length === 0 && selectedClaseId && (
          <div className="text-center py-10 text-gray-500">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-indigo-300" />
            <p className="font-medium">Hacele una consulta a Genesis AI</p>
            <p className="text-xs mt-1">Pedí resúmenes, explicaciones, preguntas, o "enviá X por email a Y"…</p>
            {speechSupported && (
              <p className="text-xs mt-2 text-indigo-600">🎤 También podés dictar tu pregunta con el botón del micrófono.</p>
            )}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
              m.role === 'user' ? 'bg-blue-500' : (m.error ? 'bg-red-500' : 'bg-gradient-to-br from-indigo-500 to-purple-600')
            }`}>
              {m.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
            </div>
            <div className="flex flex-col gap-1 max-w-[78%]">
              <div className={`rounded-2xl px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-blue-500 text-white rounded-br-sm'
                  : (m.error ? 'bg-red-50 text-red-800 rounded-bl-sm' : 'bg-white shadow-sm border border-gray-100 text-gray-800 rounded-bl-sm')
              }`}>
                <div className="whitespace-pre-wrap break-words">{m.content}</div>
              </div>
              {m.role === 'assistant' && !m.error && (
                <div className="flex gap-1 self-start">
                  {ttsSupported && (
                    <button
                      onClick={() => toggleSpeak(i, m.content)}
                      className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                        speakingIdx === i
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                      }`}
                      title={speakingIdx === i ? 'Detener lectura' : 'Leer en voz alta'}
                      data-testid={`tts-btn-${i}`}
                    >
                      {speakingIdx === i ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                      {speakingIdx === i ? 'Detener' : 'Leer'}
                    </button>
                  )}
                  {m.email_draft && (
                    <button
                      onClick={() => {
                        setEmailDraft(m.email_draft);
                        setEmailForm({
                          to: m.email_draft.to_missing ? (user?.email || '') : (m.email_draft.to || user?.email || ''),
                          cc: (m.email_draft.cc || []).join(', '),
                          subject: m.email_draft.subject || '',
                          body: m.email_draft.body || '',
                        });
                      }}
                      className="text-xs flex items-center gap-1 px-2 py-1 rounded text-emerald-700 hover:bg-emerald-50"
                      title="Ver y enviar email propuesto"
                    >
                      <Mail className="w-3 h-3" /> Enviar email
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-100 bg-white shrink-0">
        <div className="flex gap-2 items-end">
          {speechSupported && (
            <button
              onClick={toggleListening}
              disabled={!selectedClaseId || sending}
              className={`p-2 rounded-lg shrink-0 transition-all disabled:opacity-40 ${
                listening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600'
              }`}
              title={listening ? 'Detener dictado' : 'Dictar por voz'}
              data-testid="genesis-mic-btn"
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            placeholder={listening ? '🎤 Escuchando...' : (selectedClaseId ? 'Preguntale a Genesis AI…' : 'Seleccioná una clase primero')}
            disabled={!selectedClaseId || sending}
            rows={1}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none disabled:bg-gray-50 disabled:text-gray-400"
            data-testid="genesis-input"
          />
          <button
            onClick={() => enviar()}
            disabled={!input.trim() || !selectedClaseId || sending}
            className="px-3 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white disabled:opacity-50 hover:shadow-md transition-all"
            data-testid="genesis-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1 text-center">
          ⚠️ Validá las respuestas. Genesis AI puede equivocarse y vos sos la autoridad pedagógica.
        </p>
      </div>

      {/* Modal: confirmación de envío de email */}
      <Dialog open={!!emailDraft} onOpenChange={(o) => { if (!o) setEmailDraft(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="genesis-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Confirmar envío de email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-900">
              ✨ Genesis preparó este email basado en tu pedido. Revisalo, editá si hace falta, y confirmá.
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Para *</label>
              <Input
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                placeholder="destinatario@email.com"
                className="mt-1"
                data-testid="genesis-email-to"
              />
              {emailDraft?.to_missing && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Genesis no detectó el destinatario en tu pedido. Indicálo manualmente.
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">CC (opcional)</label>
                <button
                  type="button"
                  onClick={() => setCcPickerOpen((o) => !o)}
                  className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                  data-testid="genesis-cc-picker-toggle"
                >
                  {ccPickerOpen ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {ccPickerOpen ? 'Cerrar selector' : 'Elegir de la lista'}
                </button>
              </div>

              {/* Chips de emails ya seleccionados */}
              {ccEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5 p-1.5 border border-gray-200 rounded-md bg-gray-50">
                  {ccEmails.map((em) => {
                    const u = activeUsers.find((x) => x.email.toLowerCase() === em.toLowerCase());
                    return (
                      <span
                        key={em}
                        className="inline-flex items-center gap-1 bg-white border border-indigo-200 text-indigo-800 text-xs rounded-full pl-2 pr-1 py-0.5"
                        data-testid={`cc-chip-${em}`}
                      >
                        {u ? (
                          <>
                            <span className="font-medium">{u.nombre_completo}</span>
                            <span className={`text-[9px] uppercase font-bold px-1 rounded border ${rolChip(u.rol)}`}>{u.rol}</span>
                            <span className="text-gray-500 truncate max-w-[160px]">{em}</span>
                          </>
                        ) : (
                          <span>{em}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeCcEmail(em)}
                          className="ml-1 text-gray-400 hover:text-red-600"
                          aria-label="Quitar"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Selector inline de usuarios activos */}
              {ccPickerOpen && (
                <div className="mt-1.5 border border-indigo-200 rounded-md bg-indigo-50/30 p-2" data-testid="genesis-cc-picker">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <Input
                      type="text"
                      value={ccSearch}
                      onChange={(e) => setCcSearch(e.target.value)}
                      placeholder="Buscar por nombre, email o rol..."
                      className="pl-7 h-8 text-xs"
                      data-testid="genesis-cc-search"
                    />
                  </div>
                  <div className="mt-2 max-h-48 overflow-y-auto bg-white border rounded divide-y">
                    {activeUsers.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-3">Cargando usuarios...</p>
                    )}
                    {activeUsers.length > 0 && ccUsersFiltrados.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-3">Sin resultados</p>
                    )}
                    {ccUsersFiltrados.slice(0, 80).map((u) => {
                      const already = ccEmailsSet.has(u.email.toLowerCase());
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => addCcEmail(u.email)}
                          disabled={already}
                          className={`w-full px-2 py-1.5 flex items-center gap-2 text-left transition ${
                            already ? 'bg-emerald-50 cursor-default' : 'hover:bg-indigo-50'
                          }`}
                          data-testid={`cc-pick-${u.id}`}
                        >
                          {u.foto_perfil && getImageUrl ? (
                            <img
                              src={getImageUrl(u.foto_perfil)}
                              alt={u.nombre_completo}
                              className="w-6 h-6 rounded-full object-cover shrink-0"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                              {(u.nombre_completo || u.email).slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate flex items-center gap-1.5">
                              <span className="truncate">{u.nombre_completo}</span>
                              <span className={`text-[9px] uppercase font-bold px-1 py-0 rounded border ${rolChip(u.rol)}`}>{u.rol}</span>
                            </div>
                            <div className="text-[10px] text-gray-500 truncate">{u.email}</div>
                          </div>
                          {already ? (
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          ) : (
                            <Plus className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Input manual (también edita los chips por ser la misma fuente) */}
              <Input
                type="text"
                value={emailForm.cc}
                onChange={(e) => setEmailForm((f) => ({ ...f, cc: e.target.value }))}
                placeholder="O escribí emails separados por coma..."
                className="mt-1.5 text-xs"
                data-testid="genesis-email-cc"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Tip: tocá <b>"Elegir de la lista"</b> para sumar a otros usuarios con un click, o escribí emails manuales separados por coma.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Asunto</label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
                className="mt-1"
                data-testid="genesis-email-subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Cuerpo del email</label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm((f) => ({ ...f, body: e.target.value }))}
                rows={12}
                className="mt-1 font-mono text-xs"
                data-testid="genesis-email-body"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Podés usar markdown simple: ## títulos, **negritas**, - listas. El destinatario lo verá formateado.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDraft(null)} disabled={sendingEmail}>
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button
              onClick={enviarEmail}
              disabled={sendingEmail || !emailForm.to || !emailForm.body}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="genesis-email-send"
            >
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Confirmar y enviar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: confirmar borrado de conversación */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Borrar conversación
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            ¿Seguro querés borrar TODA la conversación con Genesis para esta clase?
            <br /><br />
            Esta acción <strong>no se puede deshacer</strong>. Cualquier resumen, idea o respuesta que hayas guardado se perderá.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearConfirm(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={clearConversation} data-testid="genesis-clear-confirm">
              <Trash2 className="w-4 h-4 mr-2" /> Sí, borrar todo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
